// The state and arithmetic of entering one vendor bill: which received lines it covers, the rate on each,
// and the totals. Kept apart from the screens so the bill steps share one draft.
import { useMemo, useState } from 'react';
import type { MaterialRateItem } from '../pages/OfficeEstimationPage';
import type { BillLinePayload, BillPayload, OrderRow } from '../components/MaterialReceivingDialogs';
import {
  billRatePrefill, defaultRateUnitKey, findQuoteForOrder, isReceiptBilled, normVendor, pickName, rateUnitChoices, roundMoney,
  type BillRatePrefill, type MaterialPick, type RateUnitChoice,
} from './materialReceiving';

const today = () => new Date().toISOString().slice(0, 10);
const toNum = (value: unknown) => { const n = parseFloat(String(value)); return Number.isFinite(n) ? n : 0; };
const roundRate = (value: number) => Math.round((value + Number.EPSILON) * 1000000) / 1000000;

export type BillRow = {
  key: string; order: OrderRow; receiptId: string; receiptQty: number; challan: string; receiptDate: string; gateBill: string; photoUrl?: string;
  item?: MaterialRateItem; choices: RateUnitChoice[]; defaultUnitKey: string; prefill: BillRatePrefill;
  /** Added directly while entering a bill, because the delivery was not recorded as a receipt. */
  manual?: boolean;
};
export type LineEdit = { rate?: string; unitKey?: string; update?: boolean; gstMode?: 'incl' | 'excl'; gstPercent?: string };
export type AddedBillMaterialInput = { pick?: MaterialPick | null; description: string; qty: string; unit: string };

const sameNumber = (a: string, b: string) => !!a.trim() && a.trim().toLowerCase() === b.trim().toLowerCase();
const choicesForReceipt = (base: RateUnitChoice[], receipt: any): RateUnitChoice[] => {
  const boxes = toNum(receipt?.boxes);
  const qty = toNum(receipt?.qty);
  if (boxes <= 0 || qty <= 0) return base;
  const receiptBoxChoice: RateUnitChoice = { key: 'box', label: 'Box', mult: boxes / qty };
  return [...base.filter((choice) => choice.key !== 'box'), receiptBoxChoice];
};

export function useBillDraft(orders: OrderRow[], materialItems: MaterialRateItem[]) {
  const unbilled = useMemo(() => orders.filter((order) => !order.unplannedRejected).flatMap((order) =>
    (order.receipts || []).filter((receipt) => !isReceiptBilled(receipt)).map((receipt) => ({ order, receipt }))), [orders]);
  const vendorKeys = useMemo(() => Array.from(new Set(unbilled.map(({ order }) => normVendor(order.vendor)))), [unbilled]);
  const vendorLabel = (key: string) => orders.find((order) => normVendor(order.vendor) === key)?.vendor?.trim() || 'Vendor not set';

  const [vendorKey, setVendorKey] = useState<string | null>(null);
  const [date, setDate] = useState(today());
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [edits, setEdits] = useState<Record<string, LineEdit>>({});
  const [freight, setFreight] = useState('');
  const [roundOff, setRoundOff] = useState('');
  const [roundOffManual, setRoundOffManual] = useState(false);
  const [invoiceTotal, setInvoiceTotal] = useState('');
  const [mismatchReason, setMismatchReason] = useState('');
  const [notes, setNotes] = useState('');
  const [addedRows, setAddedRows] = useState<BillRow[]>([]);
  const [billGstMode, setBillGstModeState] = useState<'incl' | 'excl'>('incl');

  const receiptRows = useMemo<BillRow[]>(() => vendorKey === null ? [] : unbilled
    .filter(({ order }) => normVendor(order.vendor) === vendorKey)
    .map(({ order, receipt }) => {
      const item = materialItems.find((candidate) => candidate.id === order.materialId);
      const choices = choicesForReceipt(rateUnitChoices(item, order), receipt);
      return {
        key: `${order.id}__${receipt.id}`, order, receiptId: receipt.id, receiptQty: receipt.qty, challan: receipt.reference || '', receiptDate: receipt.date,
        gateBill: receipt.billDetailsPending ? String(receipt.billNumber || '').trim() : '', photoUrl: receipt.photoUrl,
        item, choices, defaultUnitKey: defaultRateUnitKey(choices, order, item), prefill: billRatePrefill(item, order, date),
      };
    }), [unbilled, vendorKey, materialItems, date]);
  const rows = useMemo<BillRow[]>(() => [...receiptRows, ...addedRows], [receiptRows, addedRows]);

  const unitFor = (row: BillRow) => row.choices.find((choice) => choice.key === (edits[row.key]?.unitKey ?? row.defaultUnitKey)) || row.choices[0];
  const gstModeFor = (_row: BillRow) => billGstMode;
  const defaultGstPercentFor = (row: BillRow) => {
    const gst = toNum(findQuoteForOrder(row.item, row.order)?.gst);
    return gst > 0 ? gst : 18;
  };
  const gstPercentFor = (row: BillRow) => toNum(edits[row.key]?.gstPercent ?? defaultGstPercentFor(row));
  const gstFactorFor = (row: BillRow) => gstModeFor(row) === 'excl' ? 1 + gstPercentFor(row) / 100 : 1;
  const defaultRateText = (row: BillRow, choice: RateUnitChoice) => row.prefill.perOrderUnit > 0 ? String(roundMoney(row.prefill.perOrderUnit / choice.mult / gstFactorFor(row))) : '';
  const rateTextFor = (row: BillRow) => edits[row.key]?.rate ?? defaultRateText(row, unitFor(row));
  const perOrderUnitFor = (row: BillRow) => roundRate(toNum(rateTextFor(row)) * unitFor(row).mult * gstFactorFor(row));
  const displayRateFor = (row: BillRow, inclusivePerOrderUnit: number) => roundMoney(inclusivePerOrderUnit / gstFactorFor(row));
  const libraryDisplayRateFor = (row: BillRow) => displayRateFor(row, row.prefill.libraryPerOrderUnit);
  const enteredDisplayRateFor = (row: BillRow) => roundMoney(toNum(rateTextFor(row)) * unitFor(row).mult);
  const differsFromLibrary = (row: BillRow) => row.prefill.quoteFound && perOrderUnitFor(row) > 0 && Math.abs(perOrderUnitFor(row) - row.prefill.libraryPerOrderUnit) > Math.max(0.01, row.prefill.libraryPerOrderUnit * 0.005);
  const effectiveUpdateFor = (row: BillRow) => edits[row.key]?.update ?? differsFromLibrary(row);

  const selectedRows = rows.filter((row) => selected.has(row.key));
  const amounts = selectedRows.map((row) => roundMoney(row.receiptQty * perOrderUnitFor(row)));
  const subtotal = amounts.reduce((sum, value) => sum + value, 0);
  const beforeRound = subtotal + toNum(freight);
  const autoRound = roundMoney(Math.round(beforeRound) - beforeRound);
  const roundOffValue = roundOffManual ? toNum(roundOff) : autoRound;
  const calculatedTotal = roundMoney(beforeRound + roundOffValue);
  const difference = roundMoney(calculatedTotal - toNum(invoiceTotal));
  const mismatch = toNum(invoiceTotal) > 0 && Math.abs(difference) > 1;

  /** What is still missing before the bill can be saved, by step. */
  const missingLines: string[] = [];
  if (vendorKey === null) missingLines.push('vendor');
  else if (!selectedRows.length) missingLines.push('at least one line');
  const missingRates = selectedRows.some((row) => perOrderUnitFor(row) <= 0) ? ['a rate on every line'] : [];
  const missingTotals: string[] = [];
  if (!invoiceNumber.trim()) missingTotals.push('bill number');
  if (!date) missingTotals.push('bill date');
  if (toNum(invoiceTotal) <= 0) missingTotals.push('bill total');
  if (mismatch && !mismatchReason.trim()) missingTotals.push('a reason for the total difference');

  const setEdit = (key: string, patch: LineEdit) => setEdits((previous) => ({ ...previous, [key]: { ...previous[key], ...patch } }));
  const setBillGstMode = (mode: 'incl' | 'excl') => {
    if (mode === billGstMode) return;
    setEdits((previous) => {
      const next = { ...previous };
      selectedRows.forEach((row) => {
        const choice = row.choices.find((candidate) => candidate.key === (previous[row.key]?.unitKey ?? row.defaultUnitKey)) || row.choices[0];
        const gst = toNum(previous[row.key]?.gstPercent ?? defaultGstPercentFor(row));
        const currentFactor = billGstMode === 'excl' ? 1 + gst / 100 : 1;
        const inclusive = roundMoney(toNum(previous[row.key]?.rate ?? defaultRateText(row, choice)) * choice.mult * currentFactor);
        const nextFactor = mode === 'excl' ? 1 + gst / 100 : 1;
        next[row.key] = { ...next[row.key], rate: inclusive > 0 ? String(roundMoney(inclusive / choice.mult / nextFactor)) : '' };
      });
      return next;
    });
    setBillGstModeState(mode);
  };
  const setGstMode = (row: BillRow, mode: 'incl' | 'excl') => {
    if (mode !== billGstMode) { setBillGstMode(mode); return; }
    const inclusive = perOrderUnitFor(row);
    const choice = unitFor(row);
    const factor = mode === 'excl' ? 1 + gstPercentFor(row) / 100 : 1;
    setEdit(row.key, { gstMode: mode, rate: inclusive > 0 ? String(roundMoney(inclusive / choice.mult / factor)) : '' });
  };
  /** Lines that arrived under the same challan (or bill) number are ticked together. */
  const toggle = (key: string, checked: boolean) => {
    const row = rows.find((candidate) => candidate.key === key);
    const group = row && sameNumber(row.challan, row.challan) ? rows.filter((candidate) => sameNumber(candidate.challan, row.challan)).map((candidate) => candidate.key) : [key];
    setSelected((previous) => { const next = new Set(previous); group.forEach((entry) => { if (checked) next.add(entry); else next.delete(entry); }); return next; });
    if (checked && row?.gateBill && !invoiceNumber.trim()) setInvoiceNumber(row.gateBill);
  };
  const selectAll = (all: boolean) => setSelected(all ? new Set(rows.map((row) => row.key)) : new Set());
  const changeUnit = (row: BillRow, unitKey: string) => {
    const next = row.choices.find((choice) => choice.key === unitKey) || row.choices[0];
    const perUnit = perOrderUnitFor(row);
    setEdit(row.key, { unitKey, rate: perUnit > 0 ? String(roundMoney(perUnit / next.mult / gstFactorFor(row))) : '' });
  };

  const reset = () => {
    setVendorKey(null); setDate(today()); setInvoiceNumber(''); setSelected(new Set()); setEdits({}); setFreight(''); setRoundOff('');
    setRoundOffManual(false); setInvoiceTotal(''); setMismatchReason(''); setNotes(''); setAddedRows([]); setBillGstModeState('incl');
  };
  /** Starts a bill for one vendor (and ticks the given received lines, `orderId__receiptId`). */
  const begin = (vendor: string | null, keys: string[] = []) => {
    reset();
    if (vendor === null) return;
    setVendorKey(vendor);
    if (keys.length) {
      setSelected(new Set(keys));
      const gate = unbilled.find(({ order, receipt }) => keys.includes(`${order.id}__${receipt.id}`) && receipt.billDetailsPending && String(receipt.billNumber || '').trim());
      if (gate) setInvoiceNumber(String(gate.receipt.billNumber).trim());
    }
  };
  const chooseVendor = (key: string) => { setVendorKey(key); setSelected(new Set()); setEdits({}); setAddedRows([]); };
  const addMaterialLine = (input: AddedBillMaterialInput): string | null => {
    if (vendorKey === null) return 'Choose a vendor first.';
    const qty = toNum(input.qty);
    const pick = input.pick || null;
    const label = pick ? pick.label : input.description.trim();
    const unit = (pick ? pick.unit : input.unit.trim()) || 'Nos';
    if (!label) return 'Choose or type a material.';
    if (qty <= 0) return 'Enter quantity.';
    if (!pick && !input.unit.trim()) return 'Enter unit.';
    const item = pick?.item;
    const activeQuotes = (item?.quotes || []).filter((quote) => (quote.status || 'active') !== 'voided');
    const selectedQuoteId = String((item as any)?.selectedQuoteId || '');
    const fallbackQuoteId = selectedQuoteId || activeQuotes[activeQuotes.length - 1]?.id || '';
    const quote = findQuoteForOrder(item, { quoteId: fallbackQuoteId, vendor: vendorLabel(vendorKey) });
    const order = {
      id: '', materialId: item?.id || '', quoteOptionId: pick?.optionId || '', materialLabel: label, unit,
      vendor: vendorLabel(vendorKey), poNumber: '', quoteId: quote?.id || '', orderQty: qty, received: qty, pending: 0, over: 0,
      orderDate: date, scopeType: 'project', buildingAllocations: [], receipts: [], displayTitle: pick ? pickName(pick).title : label, displayGroup: pick ? pickName(pick).group : '',
    } as OrderRow;
    const choices = rateUnitChoices(item, order);
    const key = `added_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    const row: BillRow = {
      key, order, receiptId: '', receiptQty: qty, challan: '', receiptDate: date, gateBill: '',
      item, choices, defaultUnitKey: defaultRateUnitKey(choices, order, item), prefill: billRatePrefill(item, order, date), manual: true,
    };
    setAddedRows((previous) => [...previous, row]);
    setSelected((previous) => new Set([...Array.from(previous), key]));
    return null;
  };
  const removeMaterialLine = (key: string) => {
    setAddedRows((previous) => previous.filter((row) => row.key !== key));
    setSelected((previous) => { const next = new Set(previous); next.delete(key); return next; });
    setEdits((previous) => { const { [key]: _removed, ...rest } = previous; return rest; });
  };

  const buildPayload = (): BillPayload => {
    const lines: BillLinePayload[] = selectedRows.map((row, index) => {
      const choice = unitFor(row);
      return {
        orderId: row.manual ? '' : row.order.id, receiptId: row.receiptId, materialId: row.order.materialId, quoteId: row.order.quoteId || '', quoteOptionId: row.order.quoteOptionId || '',
        materialLabel: row.order.materialLabel, unit: row.order.unit, qty: row.receiptQty, rate: roundMoney(perOrderUnitFor(row)), amount: amounts[index],
        enteredRate: toNum(rateTextFor(row)), enteredUnit: choice.label, rateSource: edits[row.key]?.rate === undefined ? row.prefill.sourceLabel : 'Entered from bill',
        enteredGstMode: gstModeFor(row), ...(gstModeFor(row) === 'excl' ? { gstPercent: gstPercentFor(row) } : {}), ...(!row.order.materialId ? { unmapped: true } : {}),
      };
    });
    const libraryUpdates = selectedRows
      .filter((row) => !row.manual && effectiveUpdateFor(row))
      .map((row) => ({ orderId: row.order.id, ratePerOrderUnit: roundMoney(perOrderUnitFor(row)) }));
    return {
      invoiceNumber: invoiceNumber.trim(), invoiceDate: date, vendor: vendorKey === null ? '' : vendorLabel(vendorKey),
      freight: toNum(freight), roundOff: roundOffValue, invoiceTotal: toNum(invoiceTotal), calculatedTotal,
      mismatchReason: mismatch ? mismatchReason.trim() : '', notes: notes.trim(), lines, libraryUpdates,
    };
  };

  return {
    unbilled, vendorKeys, vendorLabel, vendorKey, chooseVendor, begin, reset,
    date, setDate, invoiceNumber, setInvoiceNumber, freight, setFreight, roundOff, setRoundOff, roundOffManual, setRoundOffManual,
    invoiceTotal, setInvoiceTotal, mismatchReason, setMismatchReason, notes, setNotes,
    rows, selected, selectedRows, toggle, selectAll, addedRows, addMaterialLine, removeMaterialLine, edits, setEdit, unitFor, rateTextFor, perOrderUnitFor, changeUnit,
    billGstMode, setBillGstMode, gstModeFor, setGstMode, gstPercentFor, defaultGstPercentFor, displayRateFor, libraryDisplayRateFor, enteredDisplayRateFor, differsFromLibrary, effectiveUpdateFor,
    amounts, subtotal, autoRound, roundOffValue, calculatedTotal, difference, mismatch,
    missingLines, missingRates, missingTotals, buildPayload,
  };
}
